import React from "react";
import {
  Box, Typography, Button, Paper, List, ListItem, ListItemText, ListItemIcon,
  Grid, Autocomplete, TextField, Checkbox, Collapse, useTheme, CircularProgress, Fade, IconButton, Tooltip
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import DocumentAutocompleteClone from "./DocumentAutocompleteClone";

const MultipleTab = ({
  token,
  idDocument,
  originSections,
  expandedSection,
  setExpandedSection,
  notes,
  loadingNotes,
  targetId,
  setTargetId,
  targetName,
  setTargetName,
  targetSectionId,
  setTargetSectionId,
  destinationSections,
  loadingDestSections,
  onConfirm,
  loadingMultiple,
  selectedSources,
  setSelectedSources,
}) => {
  const theme = useTheme();

  const handleToggleSection = (sectionId) => {
    setSelectedSources((prev) => {
      const next = { ...prev };
      if (next[sectionId]) {
        delete next[sectionId];
      } else {
        next[sectionId] = { cloneAll: true, notes: [] };
      }
      return next;
    });
  };

  const handleToggleNote = (sectionId, noteId) => {
    setSelectedSources((prev) => {
      const next = { ...prev };
      const currentSection = next[sectionId] || { cloneAll: false, notes: [] };
      
      let updatedNotes = [...currentSection.notes];
      if (updatedNotes.includes(noteId)) {
        updatedNotes = updatedNotes.filter((id) => id !== noteId);
      } else {
        updatedNotes.push(noteId);
      }

      if (updatedNotes.length === 0) {
        delete next[sectionId];
      } 
      
      else {
        next[sectionId] = { cloneAll: false, notes: updatedNotes };
      }

      return next;
    });
  };

  const isConfirmDisabled =
    !targetId ||
    !targetSectionId ||
    Object.keys(selectedSources).length === 0 ||
    loadingMultiple;

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} md={8}>
        <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontWeight: 600 }}>Selección de Orígenes Múltiples</Typography>
        </Box>

        <Paper elevation={0} sx={{
          border: "1px solid", borderColor: theme.palette.divider,
          borderRadius: 3, bgcolor: "background.paper"
        }}>
          <List disablePadding>
            {originSections?.map((section) => {
              const secId = section.ID_SECCION;
              const sourceConfig = selectedSources[secId];
              const isChecked = !!sourceConfig && sourceConfig.cloneAll;
              const isIndeterminate = !!sourceConfig && !sourceConfig.cloneAll && sourceConfig.notes.length > 0;

              return (
                <React.Fragment key={secId}>
                  <ListItem divider sx={{ borderColor: theme.palette.divider, display: "flex", alignItems: "center" }}>
                    <ListItemIcon>
                      <Checkbox
                        checked={isChecked}
                        indeterminate={isIndeterminate}
                        onChange={() => handleToggleSection(secId)}
                        size="small"
                        color="primary"
                      />
                    </ListItemIcon>
                    <ListItemText primary={section.NOMBRE_SECCION} />
                    
                    <Tooltip title="Ver y seleccionar notas específicas">
                      <IconButton
                        size="small"
                        onClick={() => setExpandedSection(expandedSection === secId ? null : secId)}
                      >
                        {expandedSection === secId ? <KeyboardArrowUp color="primary" /> : <KeyboardArrowDown />}
                      </IconButton>
                    </Tooltip>
                  </ListItem>

                  <Collapse in={expandedSection === secId}>
                    <Box sx={{ pl: 6, bgcolor: "action.hover", borderBottom: "1px solid #eee" }}>
                      {loadingNotes ? (
                        <Typography sx={{ p: 2, fontSize: "0.8rem" }}>Cargando notas...</Typography>
                      ) : (
                        (notes || []).length === 0 ? (
                            <Typography sx={{ p: 2, fontSize: "0.8rem", color: "text.secondary" }}>
                                No hay notas en esta sección para este día.
                            </Typography>
                        ) : (
                          (notes || []).map((nota) => {
                            const noteId = Number(nota.id_sintesis || nota.ID_SINTESIS);
                            const isNoteSelected = sourceConfig ? sourceConfig.notes.includes(noteId) || sourceConfig.cloneAll : false;
                            
                            return (
                              <ListItem 
                                key={noteId} 
                                button 
                                onClick={() => {
                                    if (sourceConfig?.cloneAll) {
                                        if (sourceConfig.cloneAll) {
                                            const allNoteIds = notes.map(n => Number(n.id_sintesis || n.ID_SINTESIS));
                                            setSelectedSources(prev => ({
                                                ...prev,
                                                [secId]: {
                                                    cloneAll: false,
                                                    notes: allNoteIds.filter(id => id !== noteId)
                                                }
                                            }));
                                        } else {
                                            handleToggleNote(secId, noteId);
                                        }
                                    } else {
                                        handleToggleNote(secId, noteId);
                                    }
                                }}
                              >
                                <ListItemIcon>
                                  <Checkbox checked={isNoteSelected} size="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={nota.titulo}
                                  secondary={`ID Síntesis: ${noteId}`}
                                />
                              </ListItem>
                            );
                          })
                        )
                      )}
                    </Box>
                  </Collapse>
                </React.Fragment>
              );
            })}
          </List>
        </Paper>
      </Grid>

      <Grid item xs={12} md={4}>
        <Paper elevation={0} sx={{
          p: 3, borderRadius: 3, border: "1px solid", borderColor: theme.palette.divider,
          bgcolor: "background.paper", position: "sticky", top: 20
        }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700 }}>
            CONFIGURACIÓN DESTINO
          </Typography>

          <DocumentAutocompleteClone
            token={token}
            documentID={targetId}
            setDocumentID={setTargetId}
            setNameDocument={setTargetName}
            excludeId={idDocument}
          />

          {targetId && (
            <Fade in={!!targetId}>
              <Box sx={{ mt: 2 }}>
                <Autocomplete
                  options={destinationSections || []}
                  getOptionLabel={(o) => o.NOMBRE_SECCION || ""}
                  loading={loadingDestSections}
                  value={destinationSections?.find((s) => s.ID_SECCION === targetSectionId) || null}
                  onChange={(e, val) => setTargetSectionId(val?.ID_SECCION || "")}
                  renderInput={(params) => (
                    <TextField {...params} label="Sección Destino" size="small" />
                  )}
                />
              </Box>
            </Fade>
          )}

          <Button
            fullWidth
            variant="contained"
            onClick={onConfirm}
            disabled={isConfirmDisabled}
            sx={{ mt: 3, py: 1.5, fontWeight: 600, borderRadius: "8px" }}
          >
            {loadingMultiple ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Confirmar Copia Múltiple"
            )}
          </Button>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default MultipleTab;
